/* eslint-disable functional/no-expression-statement,@typescript-eslint/ban-ts-comment */
import * as D3Selection from "d3-selection";
import * as D3Shape from "d3-shape";

import * as Color from "~/Color";
import { Company } from "~/Company";
import * as Log from "~/Log";
import * as Chart from "~/Log/Chart";
import { Time } from "~/Time";

const filterEventsForMilestones = (
  statusEvents: readonly Log.Chart.LogChartData[],
  driverTimezone: Time.Zone
): readonly Log.Chart.LogChartData[] =>
  statusEvents
    .filter((e) =>
      Time.DateTime.isStartOfDayForZone(
        Time.DateTime.fromNumber(e.start),
        driverTimezone
      )
    )
    .reduce((prev: readonly Log.Chart.LogChartData[], m) => {
      if (!prev.some((pm) => pm.start === m.start)) {
        return [...prev, m];
      }
      return prev;
    }, []);

const locationToAddress = (
  homeOffice?: Company.Location
): string | undefined => {
  const location = homeOffice?.location ?? undefined;
  if (location) {
    return location?.nickname
      ? location?.nickname
      : location?.address
      ? Company.Location.toString({ location })
      : undefined;
  }
  return undefined;
};

export const generate = ({
  svg,
  chartHeight,
  xScale,
  statusEvents,
  driverTimezone,
  defaultTimezone,
  driversReportingLocation,
}: Log.Chart.ChartGeneratorProps) => {
  const eventTip = D3Selection.select(".event-tip");
  const tipTitle = D3Selection.select(".tip-title");
  const tipContent = D3Selection.select(".tip-content");

  const topOffset: number = -chartHeight;

  const triangle = D3Shape.symbol().type(D3Shape.symbolTriangle).size(40);

  const milestoneEvents = filterEventsForMilestones(
    statusEvents,
    driverTimezone
  );

  const pairingGroup = svg
    .append("g")
    .attr("class", "milestone-group")
    .attr("transform", `translate(0,${chartHeight})`);

  pairingGroup
    .selectAll("line.milestone")
    .data(milestoneEvents)
    .enter()
    .append("line")
    .attr("class", "milestone")
    .attr("x1", (statusEvent: Log.Chart.LogChartData) =>
      Chart.StatusEvents.getXScaledValue(
        statusEvent.start,
        xScale,
        defaultTimezone
      )
    )
    .attr("x2", (statusEvent: Log.Chart.LogChartData) =>
      Chart.StatusEvents.getXScaledValue(
        statusEvent.start,
        xScale,
        defaultTimezone
      )
    )
    .attr("y1", 40)
    .attr("y2", 6 + topOffset)
    .attr("fill", "none")
    .attr("shape-rendering", "crispEdges")
    .attr("stroke", Color.translucentColor(Color.green(), 0.6))
    .attr("stroke-width", "2px")
    .attr("stroke-dasharray", "4 1")
    .on("mouseover", (event) => {
      eventTip.transition().duration(200).style("opacity", 1);
      tipTitle.html(
        `<span><strong>Start of Driver's Day (12 AM)</strong></span>`
      );
      tipContent.html(
        `<div>Based on the Driver's time zone ${
          driversReportingLocation &&
          `<div>&nbsp;at&nbsp;<strong>${locationToAddress(
            driversReportingLocation
          )}</strong></div>`
        }</div>`
      );
      eventTip
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        .style("left", `${event.pageX + 12}px`)
        .style("top", `${event.pageY - 8}px`);
    })
    .on("mouseout", () => {
      eventTip.transition().duration(500).style("opacity", 0);
    });

  pairingGroup
    .selectAll(".milestone-bottom")
    .data(milestoneEvents)
    .enter()
    .append("path")
    .attr("class", "milestone-bottom")
    .attr("fill", Color.translucentColor(Color.green(), 0.6))
    .attr("d", triangle)
    .attr(
      "transform",
      (d) =>
        `translate(${Chart.StatusEvents.getXScaledValue(
          d.start,
          xScale,
          defaultTimezone
        )},40)`
    );

  pairingGroup
    .selectAll(".milestone-top")
    .data(milestoneEvents)
    .enter()
    .append("path")
    .attr("class", "milestone-top")
    .attr("fill", Color.translucentColor(Color.green(), 0.6))
    .attr("stroke", Color.translucentColor(Color.green(), 0.6))
    .attr("d", triangle)
    .attr(
      "transform",
      (d) =>
        `translate(${Chart.StatusEvents.getXScaledValue(
          d.start,
          xScale,
          defaultTimezone
        )},-${chartHeight})  rotate(180)`
    );
};

export const update = ({
  svg,
  chartHeight,
  xScale,
  statusEvents,
  driverTimezone,
  defaultTimezone,
}: Log.Chart.ChartGeneratorProps): void => {
  const milestoneEvents = filterEventsForMilestones(
    statusEvents,
    driverTimezone
  );
  svg
    .selectAll("line.milestone")
    .data(milestoneEvents)
    .attr("x1", (statusEvent: Log.Chart.LogChartData) =>
      Chart.StatusEvents.getXScaledValue(
        statusEvent.start,
        xScale,
        defaultTimezone
      )
    )
    .attr("x2", (statusEvent: Log.Chart.LogChartData) =>
      Chart.StatusEvents.getXScaledValue(
        statusEvent.start,
        xScale,
        defaultTimezone
      )
    );

  svg
    .selectAll(".milestone-bottom")
    .data(milestoneEvents)
    .attr(
      "transform",
      (d) =>
        `translate(${Chart.StatusEvents.getXScaledValue(
          d.start,
          xScale,
          defaultTimezone
        )},40)`
    );

  svg
    .selectAll(".milestone-top")
    .data(milestoneEvents)
    .attr(
      "transform",
      (d) =>
        `translate(${Chart.StatusEvents.getXScaledValue(
          d.start,
          xScale,
          defaultTimezone
        )},-${chartHeight})  rotate(180)`
    );
};
